import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../../../firestore-config";
import EmptyCartView from "./EmptyCartView";
import SummaryDetails from "./SummaryDetails";
import { useStore } from "../../../app/stores/rootStore";

export default observer(function CartPage() {
    const { cartStore, userStore: { user }, bookingStore,couponStore } = useStore();
    const navigate = useNavigate();
    const [couponInput, setCouponInput] = useState("");

    // 2. Logic to calculate final total considering coupon
    const subtotal = cartStore.total;
    const discount = couponStore.calculateDiscountAmount(subtotal);
    const finalTotal = subtotal - discount;

    // Sync cart when user ID becomes available after login
    useEffect(() => {
        cartStore.syncCart(user?.uid);
    }, [user?.uid, cartStore]);

    const handleGoogleSignIn = async () => {
        try {
            await signInWithPopup(auth, googleProvider);
            // syncCart will be triggered by useEffect when user state changes in UserStore
        } catch (error) {
            console.error("Login Error:", error);
        }
    };

    const handleApplyCoupon = async () => {
        if (!couponInput) return;
        await couponStore.applyCoupon(couponInput, subtotal);
    };

        /**
     * Workflow: Save to Firestore -> WhatsApp -> Redirect to Receipt
     */
    const handleCheckout = async () => {
        if (cartStore.items.length === 0) return;

        // Requirement: Must be logged in to send WhatsApp and create booking
        if (!user) {
            alert("Please login to proceed with your order.");
            return;
        }
        const waWindow = window.open("", "_blank");
        if (waWindow) waWindow.document.write("<h4>Processing Order...</h4><p>Redirecting to WhatsApp shortly.</p>");

        try {
            // 1. Create the persistent booking in Firestore
            const booking = await bookingStore.createOrder(
                user.uid, 
                cartStore.items, 
                cartStore.total,
                couponStore.activeCoupon?.code,
                discount
            );

            // 2. Format the WhatsApp message using the Receipt Number
            const businessNumber = "+919584176284";
            let message = `*Order Confirmation - ${booking.receiptNumber}*%0A%0A`;
            
            cartStore.items.forEach((item, index) => {
                message += `${index + 1}. *${item.title}* (x${item.quantity})%0A`;
            });

            // 4. Update WhatsApp message to show discount
            if (discount > 0) {
                message += `%0A*Subtotal: Rs. ${subtotal.toLocaleString()}*`;
                message += `%0A*Discount (${couponStore.activeCoupon?.code}): -Rs. ${discount.toLocaleString()}*`;
            }
            message += `%0A*Final Total: Rs. ${finalTotal.toLocaleString()}*%0A`;
            message += `Customer Email: ${user.email}`;


            const whatsappUrl = `https://wa.me/${businessNumber}?text=${message}`;

            // 4. REDIRECT WINDOW OR FALLBACK
            if (waWindow) {
                waWindow.location.href = whatsappUrl;
            } else {
                window.open(whatsappUrl, "_blank");
            }

            // 4. Clear the cart (ensuring DB is updated)
            await cartStore.clearCart(user.uid);

            // 5. Navigate to the Booking Receipt page
            navigate(`/receipt/${booking.id}`);

        }  catch (error) {
            if (waWindow) waWindow.close();
            console.error("Checkout failed:", error);
            alert("Failed to process order. Please check your connection.");
        }
    };

    if (!cartStore.isLoaded) return <div className="text-center py-5">Loading Cart...</div>;

    return (        
        <div className="container py-4" style={{ marginTop: '110px' }}>
            <h2 className="fw-bold mb-4">Your Shopping Cart ({cartStore.itemCount})</h2>
            
            <div className="row g-4">
                {/* Product List (Mobile Stacked, Desktop Side-by-Side) */}
                <div className="col-lg-8">
                    <div className="d-flex flex-column gap-3">
                        {cartStore.items.map((item) => (
                            <div key={item.id} className="card border-0 shadow-sm rounded-4 overflow-hidden p-3">
                                <div className="row align-items-center g-3">
                                    {/* Media Column */}
                                    <div className="col-4 col-md-2">
                                        {item.displayMode === "video" ? (
                                            <video className="rounded w-100" autoPlay loop muted style={{ aspectRatio: '1/1', objectFit: 'cover' }}>
                                                <source src={item.mainImageUrl} type="video/mp4" />
                                            </video>
                                        ) : (
                                            <img src={item.mainImageUrl} className="rounded w-100" style={{ aspectRatio: '1/1', objectFit: 'cover' }} alt="" />
                                        )}
                                    </div>

                                    {/* Info Column */}
                                    <div className="col-8 col-md-4">
                                        <h6 className="fw-bold mb-1">{item.title}</h6>
                                        <p className="text-muted small mb-0">Rs. {item.productPrice?.toLocaleString()}</p>
                                        <button className="btn btn-sm btn-link text-danger p-0 mt-1 d-md-none" onClick={() => cartStore.delete(item.id)}>Remove</button>
                                    </div>

                                    {/* Quantity & Price Column */}
                                    <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
                                        <div className="btn-group btn-group-sm border rounded-pill overflow-hidden">
                                            <button className="btn btn-light" onClick={() => cartStore.updateQty(item.id, -1, user?.uid)}>-</button>
                                            <span className="btn bg-white border-0 disabled text-dark fw-bold px-3">{item.quantity}</span>
                                            <button className="btn btn-light" onClick={() => cartStore.updateQty(item.id, 1, user?.uid)}>+</button>
                                        </div>
                                        <div className="text-end">
                                            <div className="fw-bold text-primary fs-5">Rs. {(item.productPrice * item.quantity).toLocaleString()}</div>
                                            <button className="btn btn-link text-danger d-none d-md-block p-0 small" onClick={() => cartStore.delete(item.id)}>Remove</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Sidebar (Sticky on Web) */}
                <div className="col-lg-4">
                    <div className="sticky-top" style={{ top: '100px' }}>
                        {/* Promotions */}
                        <div className="card shadow-sm p-3 border-0 rounded-4 mb-3">
                            <h6 className="fw-bold mb-3">Promotions</h6>
                            <div className="input-group mb-2">
                                <input type="text" className="form-control border-0 bg-light" placeholder="Coupon Code" value={couponInput} onChange={(e) => setCouponInput(e.target.value)} />
                                <button className="btn btn-dark" onClick={handleApplyCoupon}>Apply</button>
                            </div>
                            {couponStore.error && <small className="text-danger mt-2">{couponStore.error}</small>}
                            {couponStore.activeCoupon && (
                                <div className="badge bg-success-subtle text-success p-2 w-100 d-flex justify-content-between align-items-center">
                                    <span>{couponStore.activeCoupon.code} Applied</span>
                                    <i className="bi bi-x-lg pointer" onClick={() => couponStore.clearCoupon()}></i>
                                </div>
                            )}
                        </div>

                        {/* Summary */}
                        <div className="card shadow-sm p-4 border-0 rounded-4 bg-white">
                            <h5 className="fw-bold mb-4">Order Summary</h5>
                            <SummaryDetails subtotal={subtotal} discount={discount} />
                            <hr className="my-4" />
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <span className="fw-bold fs-5">Total</span>
                                <span className="fw-bold text-primary fs-3">Rs. {finalTotal.toLocaleString()}</span>
                            </div>                            
                            {!user ? (
                                <button onClick={handleGoogleSignIn} className="btn btn-dark w-100 fw-bold">
                                    Login with Google to Checkout
                                </button>
                            ) : (                            
                                <button className="btn btn-success btn-lg w-100 rounded-pill mb-3 fw-bold py-3 shadow-smbtn btn-success w-100 fw-bold" onClick={handleCheckout}
                                    disabled={cartStore.items.length === 0 || !user}>
                                    Proceed to WhatsApp
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>

    );
});